import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import maplibregl from 'maplibre-gl';
import type {
  GeoJSONSource,
  LngLatBoundsLike,
  MapGeoJSONFeature,
  MapMouseEvent,
} from 'maplibre-gl';
import type { Feature, FeatureCollection, Point } from 'geojson';
import { AppShell } from '../components/AppShell';
import { api } from '../api/client';
import { themeSignal } from '../theme/theme';
import type {
  AlarmLevel,
  MapAssetDto,
  MapFilterSpec,
  MapForecastDto,
  MapWeatherDto,
  SuggestMapFilterResponse,
  SavedMapViewDto,
  SavedMapViewUpsertRequest,
} from '../api/types';
import { showToast } from '../components/Toaster';
import './MapPage.css';
import { DEVICE_CATEGORIES, iconKey, iconSvg } from '../components/deviceCategories';
import { addWeatherImages, formatTemp, TEMP_STOPS, WEATHER_LABEL, weatherSvg } from './weatherIcons';
import type { WeatherCondition } from './weatherIcons';
import { clusterSvg, equipmentText, markerHtml, markerIcons, plantState, STATE_LABEL } from './mapMarkers';
import type { MarkerProps, PlantState } from './mapMarkers';

// Self gehostete Protomaps-Vector-Tiles (Europe subset), Style baut sich zur
// Laufzeit ueber @protomaps/basemaps zusammen. Kein Third-Party-CDN im
// Runtime-Pfad, kein API-Key, keine Rate-Limits — siehe pages/mapStyle.ts.
import { buildMapStyle } from './mapStyle';

// Default view when the map first opens or no plant has coordinates: centred
// on continental Europe so the operator immediately sees the multi-country
// context. fitBounds later zooms in once plant coords are known (capped by
// MAX_FIT_ZOOM below).
const EUROPE_CENTER: [number, number] = [12.0, 50.5];
const EUROPE_ZOOM = 4;
const MAX_FIT_ZOOM = 6;

const SOURCE_ID = 'plants';
// Auffaechern: Symbole naeher als eine Zelle gelten als aufeinanderliegend.
const FAN_CELL_PX = 30;
const FAN_RADIUS_PX = 26;
const REGIONS_SOURCE = 'hv-regions';
const NUTS3_SOURCE = 'hv-nuts3';

/*
 * Kartenansicht (23.10.2026, Patrick): wie stark Anlagen zu Gruppen
 * zusammengefasst werden und welche Grenzen zu sehen sind. Pro Betrachter
 * im Browser gemerkt, nicht Teil einer gespeicherten Filteransicht.
 * Stufe = Radius in Pixeln und Zoomstufe, ab der nicht mehr gruppiert wird
 * (Zoom 5 etwa Mitteleuropa, 9 etwa ein Bundesland, 12 eine Stadt).
 */
const CLUSTER_STEPS: { label: string; radius: number; maxZoom: number }[] = [
  { label: 'Nie zusammenfassen', radius: 0, maxZoom: 0 },
  { label: 'Nur aus großer Höhe (Europa)', radius: 30, maxZoom: 5 },
  { label: 'Bis Länderebene', radius: 35, maxZoom: 7 },
  { label: 'Bis Bundeslandebene', radius: 40, maxZoom: 9 },
  { label: 'Bis Bezirksebene', radius: 50, maxZoom: 11 },
  { label: 'Bis Stadtebene', radius: 55, maxZoom: 13 },
];
type BorderMode = 'none' | 'regions' | 'nuts3';
/** Welcher Wert die Flaechen faerbt und unter dem Symbol steht. */
type WeatherValue = 'current' | 'night0' | 'night1' | 'max0' | 'max1';
/*
 * Darstellung der Einfaerbungen (Patrick, 23.10.2026): jeder Benutzer
 * entscheidet selbst, ob Flaeche, Umrandung oder beides, wie kraeftig und
 * bei der Hoehe in welcher Farbe. Die Temperatur behaelt ihre Skala, dort
 * traegt die Farbe die Information. Hoehe "auto": Flaeche, solange kein
 * Wetter eingefaerbt ist, sonst Umrandung, damit sich die Farben nicht
 * vermischen.
 */
type OverlayStyle = 'fill' | 'outline' | 'both';
type ElevStyle = OverlayStyle | 'auto';
type Strength = 'low' | 'mid' | 'high';
const STRENGTH: Record<Strength, number> = { low: 0.6, mid: 1, high: 1.5 };
const ELEV_COLORS: [string, string][] = [
  ['#a0612d', 'Erdton'], ['#7b5cc4', 'Violett'], ['#3f8f5a', 'Grün'], ['#6b7280', 'Grau'],
];
interface MapViewSettings {
  cluster: number; borders: BorderMode; weather: boolean; wxValue: WeatherValue; wxIcons: boolean;
  elevStyle: ElevStyle; elevStrength: Strength; elevColor: string;
  wxStyle: OverlayStyle; wxStrength: Strength;
}
const WEATHER_VALUES: [WeatherValue, string][] = [
  ['current', 'Aktuell'], ['night0', 'Tief heute Nacht'], ['night1', 'Tief morgen Nacht'],
  ['max0', 'Hoch heute'], ['max1', 'Hoch morgen'],
];
const MAP_VIEW_KEY = 'hv.map.ansicht';
const DEFAULT_VIEW: MapViewSettings = {
  cluster: 3, borders: 'regions', weather: true, wxValue: 'night0', wxIcons: true,
  elevStyle: 'auto', elevStrength: 'mid', elevColor: '#a0612d',
  wxStyle: 'fill', wxStrength: 'mid',
};

/** Gespeicherte Ansicht pruefen; Unbekanntes faellt auf die Vorgabe. */
function normalizeMapView(input: unknown): MapViewSettings {
  const raw = input as Partial<MapViewSettings> | null;
  {
    if (raw && typeof raw === 'object') {
      const cluster = typeof raw.cluster === 'number' && raw.cluster >= 0 && raw.cluster < CLUSTER_STEPS.length
        ? raw.cluster : DEFAULT_VIEW.cluster;
      const borders = raw.borders === 'none' || raw.borders === 'regions' || raw.borders === 'nuts3'
        ? raw.borders : DEFAULT_VIEW.borders;
      const wxValue = WEATHER_VALUES.some(([k]) => k === raw.wxValue) ? raw.wxValue as WeatherValue : DEFAULT_VIEW.wxValue;
      return {
        cluster, borders, wxValue,
        weather: typeof raw.weather === 'boolean' ? raw.weather : DEFAULT_VIEW.weather,
        wxIcons: typeof raw.wxIcons === 'boolean' ? raw.wxIcons : DEFAULT_VIEW.wxIcons,
        elevStyle: (['auto', 'fill', 'outline', 'both'] as string[]).includes(String(raw.elevStyle)) ? raw.elevStyle as ElevStyle : DEFAULT_VIEW.elevStyle,
        elevStrength: raw.elevStrength && raw.elevStrength in STRENGTH ? raw.elevStrength : DEFAULT_VIEW.elevStrength,
        elevColor: ELEV_COLORS.some(([c]) => c === raw.elevColor) ? String(raw.elevColor) : DEFAULT_VIEW.elevColor,
        wxStyle: (['fill', 'outline', 'both'] as string[]).includes(String(raw.wxStyle)) ? raw.wxStyle as OverlayStyle : DEFAULT_VIEW.wxStyle,
        wxStrength: raw.wxStrength && raw.wxStrength in STRENGTH ? raw.wxStrength : DEFAULT_VIEW.wxStrength,
      };
    }
  }
  return DEFAULT_VIEW;
}

/** Erster Stand aus dem Browser (schnell), danach gilt der Server. */
function readMapView(): MapViewSettings {
  try {
    return normalizeMapView(JSON.parse(localStorage.getItem(MAP_VIEW_KEY) ?? 'null'));
  } catch { /* privates Fenster oder kaputter Eintrag */ }
  return DEFAULT_VIEW;
}

/** Schluessel der Kartenansicht in /api/me/preferences (je Benutzer am Server). */
const MAP_VIEW_PREF = 'map.view';

function writeMapView(v: MapViewSettings): void {
  try { localStorage.setItem(MAP_VIEW_KEY, JSON.stringify(v)); } catch { /* dann eben nicht */ }
}

function clusterOptions(step: number): { cluster: boolean; clusterRadius?: number; clusterMaxZoom?: number } {
  const s = CLUSTER_STEPS[step] ?? CLUSTER_STEPS[DEFAULT_VIEW.cluster];
  return s.radius === 0 ? { cluster: false } : { cluster: true, clusterRadius: s.radius, clusterMaxZoom: s.maxZoom };
}

const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] };

/*
 * Hoehenbaender (23.10.2026): mit Hoehenfilter faerbt die Karte Flaechen nach
 * ihrem Anteil im Band ein. Ebene nach Zoom: Laender von weit weg,
 * Bundeslaender und Kantone naeher, NUTS-3-Regionen ganz nah (die Schweiz hat
 * keine NUTS-3 mit eigener Zuordnung, dort bleiben die Kantone).
 * `elevation.above[i]` ist der Flaechenanteil ab i*100 m (nuts_hoehe.py).
 */
const ELEV_LEVELS: { id: string; minzoom: number; maxzoom: number }[] = [
  { id: 'hv-elev-0', minzoom: 0, maxzoom: 4.5 },
  { id: 'hv-elev-1', minzoom: 4.5, maxzoom: 7 },
  { id: 'hv-elev-2', minzoom: 7, maxzoom: 24 },
];

interface ElevationStats { min: number; mean: number; max: number; above: number[] }

function elevationOf(props: Record<string, unknown> | null): ElevationStats | null {
  const raw = props?.elevation;
  const e = (typeof raw === 'string' ? JSON.parse(raw) : raw) as ElevationStats | null | undefined;
  return e && Array.isArray(e.above) ? e : null;
}

/** Anteil der Flaeche zwischen min und max (auf 100 m gerundet). */
function bandShare(e: ElevationStats, min: number | null | undefined, max: number | null | undefined): number {
  const at = (h: number) => {
    const i = Math.max(0, Math.round(h / 100));
    return i >= e.above.length ? 0 : e.above[i];
  };
  const lo = min == null ? 1 : at(min);
  const hi = max == null ? 0 : at(max);
  return Math.max(0, Math.min(1, lo - hi));
}

/*
 * Wetterebene (23.10.2026): Flaechen nach Temperatur, Symbol mit Wert im
 * Punkt der Flaeche, Ebene nach Zoom wie bei den Hoehenbaendern. Mit
 * Nachttief-Filter werden die Flaechen kraeftig, die die Schwelle erreichen,
 * der Rest blass.
 */
const WX_LEVELS = ELEV_LEVELS.map((l, i) => ({ ...l, id: `hv-wx-${i}`, pt: `hv-wxp-${i}` }));

function wxValueOf(f: MapForecastDto, v: WeatherValue): { t: number | null; cond: string } {
  switch (v) {
    case 'current': return { t: f.currentC, cond: f.current };
    case 'night0': return { t: f.days[0]?.nightMinC ?? null, cond: f.days[0]?.condition ?? 'Unknown' };
    case 'night1': return { t: f.days[1]?.nightMinC ?? null, cond: f.days[1]?.condition ?? 'Unknown' };
    case 'max0': return { t: f.days[0]?.maxC ?? null, cond: f.days[0]?.condition ?? 'Unknown' };
    case 'max1': return { t: f.days[1]?.maxC ?? null, cond: f.days[1]?.condition ?? 'Unknown' };
  }
}

function boundaryKey(props: Record<string, unknown> | null, level: number): string[] {
  const id = String(props?.nuts3Code ?? props?.regionIso ?? props?.countryIso2 ?? '');
  // Kantone stehen in der NUTS-3-Ebene mit, ihr Wetter liegt unter precision 1.
  return [`${level}:${id}`, `1:${id}`];
}

function weatherSets(
  b: { countries: FeatureCollection | null; regions: FeatureCollection; nuts3: FeatureCollection | null },
  wx: MapWeatherDto | null,
  view: MapViewSettings,
  f: MapFilterSpec,
): { fills: FeatureCollection[]; points: FeatureCollection[] } {
  const empty = { fills: WX_LEVELS.map(() => EMPTY_FC), points: WX_LEVELS.map(() => EMPTY_FC) };
  if (!view.weather || !wx) return empty;
  const byKey = new Map(wx.regions.map((r) => [r.key, r]));
  const ch = b.regions.features.filter((x) => (x.properties as Record<string, unknown> | null)?.countryIso2 === 'CH');
  const sets = [b.countries ?? EMPTY_FC, b.regions, { type: 'FeatureCollection', features: [...(b.nuts3?.features ?? []), ...ch] } as FeatureCollection];
  const threshold = f.maxNightMinC;
  const night = Math.max(0, Math.min(2, f.nightIndex ?? 0));
  const fills: FeatureCollection[] = [];
  const points: FeatureCollection[] = [];
  sets.forEach((fc, level) => {
    const polys: Feature[] = [];
    const pts: Feature[] = [];
    for (const feat of fc.features) {
      const props = feat.properties as Record<string, unknown> | null;
      const r = boundaryKey(props, level).map((k) => byKey.get(k)).find(Boolean);
      if (!r) continue;
      const { t, cond } = wxValueOf(r.forecast, view.wxValue);
      const nightMin = r.forecast.days[night]?.nightMinC ?? null;
      const hl = threshold == null ? 1 : nightMin != null && nightMin <= threshold ? 1 : 0;
      polys.push({ type: 'Feature', geometry: feat.geometry, properties: { name: props?.name ?? '', t: t ?? -999, hasT: t != null, hl } });
      const label = view.wxValue === 'current' ? formatTemp(t)
        : `${formatTemp(r.forecast.days[view.wxValue.endsWith('1') ? 1 : 0]?.nightMinC)} / ${formatTemp(r.forecast.days[view.wxValue.endsWith('1') ? 1 : 0]?.maxC)}`;
      pts.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [r.lon, r.lat] }, properties: { name: props?.name ?? '', cond, label, hl } });
    }
    fills.push({ type: 'FeatureCollection', features: polys });
    points.push({ type: 'FeatureCollection', features: pts });
  });
  return { fills, points };
}

function installWeatherLayers(map: maplibregl.Map): void {
  if (map.getSource(WX_LEVELS[0].id)) return;
  const tempColor: maplibregl.ExpressionSpecification = ['interpolate', ['linear'], ['get', 't'], ...TEMP_STOPS.flat()] as unknown as maplibregl.ExpressionSpecification;
  for (const lvl of WX_LEVELS) {
    map.addSource(lvl.id, { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: `${lvl.id}-fill`, type: 'fill', source: lvl.id, minzoom: lvl.minzoom, maxzoom: lvl.maxzoom,
      filter: ['==', ['get', 'hasT'], true],
      paint: { 'fill-color': tempColor, 'fill-opacity': ['case', ['==', ['get', 'hl'], 1], 0.5, 0.08] },
    });
    map.addLayer({
      id: `${lvl.id}-line`, type: 'line', source: lvl.id, minzoom: lvl.minzoom, maxzoom: lvl.maxzoom,
      filter: ['==', ['get', 'hasT'], true],
      layout: { visibility: 'none' },
      paint: { 'line-color': tempColor, 'line-width': ['case', ['==', ['get', 'hl'], 1], 2.4, 0.8], 'line-opacity': ['case', ['==', ['get', 'hl'], 1], 0.95, 0.3] },
    });
  }
}

/** Darstellung von Hoehe und Wetter nach den Einstellungen des Benutzers. */
function applyOverlayStyle(map: maplibregl.Map, view: MapViewSettings, weatherActive: boolean): void {
  if (!map.getLayer(`${ELEV_LEVELS[0].id}-fill`)) return;
  const vis = (on: boolean) => (on ? 'visible' : 'none');
  const elevStyle: OverlayStyle = view.elevStyle === 'auto' ? (weatherActive ? 'outline' : 'fill') : view.elevStyle;
  const ef = STRENGTH[view.elevStrength];
  for (const lvl of ELEV_LEVELS) {
    const fill = `${lvl.id}-fill`, line = `${lvl.id}-line`;
    map.setLayoutProperty(fill, 'visibility', vis(elevStyle !== 'outline'));
    map.setPaintProperty(fill, 'fill-color', view.elevColor);
    map.setPaintProperty(fill, 'fill-opacity',
      ['interpolate', ['linear'], ['get', 'share'], 0, 0.06 * ef, 0.25, 0.25 * ef, 1, Math.min(0.85, 0.6 * ef)]);
    map.setPaintProperty(line, 'line-color', view.elevColor);
    if (elevStyle === 'fill') {
      map.setPaintProperty(line, 'line-width', 0.8);
      map.setPaintProperty(line, 'line-opacity', 0.55);
    } else {
      // Umrandung allein traegt den Anteil: breiter und kraeftiger, je mehr
      // der Flaeche im Band liegt.
      map.setPaintProperty(line, 'line-width', ['interpolate', ['linear'], ['get', 'share'], 0, 0.8, 1, 3.2 * Math.min(1.3, ef)]);
      map.setPaintProperty(line, 'line-opacity', ['interpolate', ['linear'], ['get', 'share'], 0, 0.3 * ef, 1, Math.min(1, 0.95 * ef)]);
    }
  }
  const wf = STRENGTH[view.wxStrength];
  for (const lvl of WX_LEVELS) {
    map.setLayoutProperty(`${lvl.id}-fill`, 'visibility', vis(view.wxStyle !== 'outline'));
    map.setLayoutProperty(`${lvl.id}-line`, 'visibility', vis(view.wxStyle !== 'fill'));
    map.setPaintProperty(`${lvl.id}-fill`, 'fill-opacity',
      ['case', ['==', ['get', 'hl'], 1], Math.min(0.85, 0.5 * wf), 0.08 * wf]);
  }
}

function installWeatherSymbols(map: maplibregl.Map): void {
  if (map.getSource(WX_LEVELS[0].pt)) return;
  const text = readCssVar('--text-primary', '#1d232b');
  const halo = readCssVar('--surface-base', '#ffffff');
  for (const lvl of WX_LEVELS) {
    map.addSource(lvl.pt, { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: `${lvl.pt}-sym`, type: 'symbol', source: lvl.pt, minzoom: lvl.minzoom, maxzoom: lvl.maxzoom,
      layout: {
        'icon-image': ['concat', 'wx-', ['get', 'cond']],
        'icon-size': 1,
        'icon-allow-overlap': false,
        'text-field': ['get', 'label'],
        'text-size': 11,
        'text-offset': [0, 1.55],
        'text-anchor': 'top',
        'text-optional': true,
        'symbol-sort-key': ['-', 1, ['get', 'hl']],
      },
      paint: {
        'text-color': text, 'text-halo-color': halo, 'text-halo-width': 1.4,
        'icon-opacity': ['case', ['==', ['get', 'hl'], 1], 1, 0.35],
        'text-opacity': ['case', ['==', ['get', 'hl'], 1], 1, 0.35],
      },
    });
  }
}

function weatherPopupHtml(f: MapForecastDto | null | undefined): string {
  if (!f || f.days.length === 0) return '';
  const d0 = f.days[0], d1 = f.days[1];
  const cond = (c: string) => WEATHER_LABEL[c as WeatherCondition] ?? c;
  return `<dt>Wetter</dt><dd class="hv-map__popup-wx">${weatherSvg((d0.condition || 'Unknown') as WeatherCondition, 20)}<span>`
    + `jetzt ${formatTemp(f.currentC)}, Tief heute Nacht ${formatTemp(d0.nightMinC)}`
    + `${d1 ? `<br>morgen ${formatTemp(d1.minC)} bis ${formatTemp(d1.maxC)}, ${escapeHtml(cond(d1.condition))}` : ''}</span></dd>`;
}

function elevationSets(
  b: { countries: FeatureCollection | null; regions: FeatureCollection; nuts3: FeatureCollection | null },
  f: MapFilterSpec,
): FeatureCollection[] {
  const ch = b.regions.features.filter((x) => (x.properties as Record<string, unknown> | null)?.countryIso2 === 'CH');
  const nah: FeatureCollection = { type: 'FeatureCollection', features: [...(b.nuts3?.features ?? []), ...ch] };
  return [elevationData(b.countries ?? EMPTY_FC, f), elevationData(b.regions, f), elevationData(nah, f)];
}

function hasElevationFilter(f: MapFilterSpec): boolean {
  return f.minElevationM != null || f.maxElevationM != null;
}

function elevationData(fc: FeatureCollection, f: MapFilterSpec): FeatureCollection {
  if (!hasElevationFilter(f)) return EMPTY_FC;
  return {
    type: 'FeatureCollection',
    features: fc.features.flatMap((feat) => {
      const e = elevationOf(feat.properties as Record<string, unknown> | null);
      if (!e) return [];
      return [{ ...feat, properties: { ...feat.properties, elevation: null, share: bandShare(e, f.minElevationM, f.maxElevationM), elevMin: e.min, elevMean: e.mean, elevMax: e.max } }];
    }),
  };
}

/**
 * Grenzen unter den Anlagen: Linien je Bundesland/Kanton (und auf Wunsch
 * NUTS-3-Regionen) sowie die im Filter gewaehlten Bundeslaender bzw. Laender
 * als Flaeche. Hervorhebung in der Messwertfarbe, nicht der Markenfarbe
 * (bei Austria Email rot, das hiesse Alarm).
 */
function installBoundaryLayers(map: maplibregl.Map, regions: FeatureCollection, nuts3: FeatureCollection, elev: FeatureCollection[]): void {
  if (map.getSource(REGIONS_SOURCE)) return;
  installWeatherLayers(map);
  const line = readCssVar('--text-secondary', '#8a929e');
  const hl = readCssVar('--hmi-value', readCssVar('--status-info', '#4fa3e1'));
  ELEV_LEVELS.forEach((lvl, i) => {
    map.addSource(lvl.id, { type: 'geojson', data: elev[i] ?? EMPTY_FC });
    map.addLayer({
      id: `${lvl.id}-fill`, type: 'fill', source: lvl.id, minzoom: lvl.minzoom, maxzoom: lvl.maxzoom,
      filter: ['>', ['get', 'share'], 0.005],
      paint: {
        'fill-color': DEFAULT_VIEW.elevColor,
        // 0 % durchsichtig, 100 % kraeftig; kleine Anteile bleiben sichtbar.
        'fill-opacity': ['interpolate', ['linear'], ['get', 'share'], 0, 0.06, 0.25, 0.25, 1, 0.6],
      },
    });
    map.addLayer({
      id: `${lvl.id}-line`, type: 'line', source: lvl.id, minzoom: lvl.minzoom, maxzoom: lvl.maxzoom,
      filter: ['>', ['get', 'share'], 0.005],
      paint: { 'line-color': DEFAULT_VIEW.elevColor, 'line-opacity': 0.55, 'line-width': 0.8 },
    });
  });
  map.addSource(REGIONS_SOURCE, { type: 'geojson', data: regions });
  map.addSource(NUTS3_SOURCE, { type: 'geojson', data: nuts3 });
  map.addLayer({
    id: 'hv-regions-hl-fill', type: 'fill', source: REGIONS_SOURCE,
    filter: ['==', ['get', 'regionIso'], '__keine__'],
    paint: { 'fill-color': hl, 'fill-opacity': 0.14 },
  });
  map.addLayer({
    id: 'hv-nuts3-line', type: 'line', source: NUTS3_SOURCE,
    layout: { visibility: 'none' },
    paint: { 'line-color': line, 'line-opacity': 0.45, 'line-width': 0.8, 'line-dasharray': [3, 2] },
  });
  map.addLayer({
    id: 'hv-regions-line', type: 'line', source: REGIONS_SOURCE,
    layout: { visibility: 'none' },
    paint: { 'line-color': line, 'line-opacity': 0.6, 'line-width': 1.2 },
  });
  map.addLayer({
    id: 'hv-regions-hl-line', type: 'line', source: REGIONS_SOURCE,
    filter: ['==', ['get', 'regionIso'], '__keine__'],
    paint: { 'line-color': hl, 'line-opacity': 0.9, 'line-width': 2.2 },
  });
}

/** Sichtbarkeit der Grenzlinien und gewaehlte Flaechen nachfuehren. */
function applyBoundaryView(map: maplibregl.Map, borders: BorderMode, filter: MapFilterSpec): void {
  if (!map.getLayer('hv-regions-line')) return;
  map.setLayoutProperty('hv-regions-line', 'visibility', borders === 'none' ? 'none' : 'visible');
  map.setLayoutProperty('hv-nuts3-line', 'visibility', borders === 'nuts3' ? 'visible' : 'none');
  const regions = filter.regionIso ?? [];
  const countries = filter.countryIso2 ?? [];
  // Bundeslaender gewaehlt: genau die. Sonst ganze gewaehlte Laender.
  const sel: maplibregl.FilterSpecification = regions.length > 0
    ? ['in', ['get', 'regionIso'], ['literal', regions]]
    : countries.length > 0
      ? ['in', ['get', 'countryIso2'], ['literal', countries]]
      : ['==', ['get', 'regionIso'], '__keine__'];
  map.setFilter('hv-regions-hl-fill', sel);
  map.setFilter('hv-regions-hl-line', sel);
}
const LAYER_CLUSTERS = 'plants-clusters';
const LAYER_CLUSTER_COUNT = 'plants-cluster-count';
const LAYER_UNCLUSTERED = 'plants-unclustered';

/**
 * Read an accent-ish colour from CSS custom properties so cluster/point paint
 * matches the tenant brand. Falls back to a neutral blue if the token isn't
 * set on the document root at render time.
 */
function readCssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value.length > 0 ? value : fallback;
}

type PlantFeature = Feature<Point, {
  id: string;
  displayName: string;
  alarm: AlarmLevel;
  online: boolean;
  elevationM: number | null;
  deviceKind: string | null;
  osKind: string | null;
  // Kartensymbol (mapMarkers.ts). subs als Komma-Liste: GeoJSON-Eigenschaften
  // kommen aus querySourceFeatures flach zurueck.
  state: PlantState;
  main: string;
  subs: string;
  more: number;
  alarmCount: number;
  service: boolean;
  equipment: string;
  weatherHtml: string;
}>;

function toFeatureCollection(assets: MapAssetDto[]): FeatureCollection<Point> {
  const features: PlantFeature[] = [];
  for (const a of assets) {
    if (typeof a.lat !== 'number' || typeof a.lon !== 'number') continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [a.lon, a.lat] },
      properties: {
        id: a.id,
        displayName: a.name,
        alarm: a.alarm,
        online: a.online,
        elevationM: a.elevationM,
        deviceKind: a.deviceKind,
        osKind: a.osKind,
        state: plantState(a),
        ...(() => { const m = markerIcons(a.equipment); return { main: m.main, subs: m.subs.join(','), more: m.more }; })(),
        alarmCount: a.alarmCount ?? 0,
        service: a.serviceActive === true,
        equipment: equipmentText(a.equipment),
        weatherHtml: weatherPopupHtml(a.weather),
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

/**
 * Attach the plants source + layers. Pin colour is chosen by AlarmLevel so
 * the operator can spot a red panel at first glance without having to click
 * or filter. Clusters keep the brand accent because a cluster aggregates
 * mixed alarm levels; we render an alarm ring around the cluster when it
 * contains at least one alarming plant (see `clusterProperties`).
 */
function installPlantsLayers(
  map: maplibregl.Map,
  data: FeatureCollection<Point>,
  accent: string,
  ringColor: string,
  labelColor: string,
  warnColor: string,
  errorColor: string,
  clusterStep: number,
): void {
  if (map.getSource(SOURCE_ID)) return;

  map.addSource(SOURCE_ID, {
    type: 'geojson',
    data,
    // Stufe aus der Kartenansicht; der Cluster-Ring braucht die Properties
    // auch dann, wenn spaeter per setClusterOptions eingeschaltet wird.
    ...clusterOptions(clusterStep),
    // Aggregate the worst alarm level inside each cluster so the ring can
    // colour by "has an active alarm in there". MapLibre reduces are:
    // (accumulated, current) → accumulated. Encode None/Warning/Alarm/Critical
    // as 0/1/2/3 and take the max.
    clusterProperties: {
      // Anteile fuer den Ring des Cluster-Symbols (mapMarkers.clusterSvg).
      nAlarm: ['+', ['case', ['==', ['get', 'state'], 'alarm'], 1, 0]],
      nWarn: ['+', ['case', ['==', ['get', 'state'], 'warn'], 1, 0]],
      nOffline: ['+', ['case', ['==', ['get', 'state'], 'offline'], 1, 0]],
      worstAlarm: ['max', ['case',
        ['==', ['get', 'alarm'], 'Critical'], 3,
        ['==', ['get', 'alarm'], 'Alarm'], 2,
        ['==', ['get', 'alarm'], 'Warning'], 1,
        0,
      ]],
    },
  });

  map.addLayer({
    id: LAYER_CLUSTERS,
    type: 'circle',
    source: SOURCE_ID,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': accent,
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        17,
        10, 20,
        50, 24,
        200, 28,
      ],
      'circle-stroke-width': 3,
      'circle-stroke-color': [
        'case',
        ['>=', ['get', 'worstAlarm'], 2], errorColor,
        ['>=', ['get', 'worstAlarm'], 1], warnColor,
        ringColor,
      ],
      // Gezeichnet wird das HTML-Symbol (mapMarkers.ts); die Ebene bleibt
      // als Trefferflaeche fuer Klick und Mauszeiger.
      'circle-opacity': 0,
      'circle-stroke-opacity': 0,
    },
  });

  map.addLayer({
    id: LAYER_CLUSTER_COUNT,
    type: 'symbol',
    source: SOURCE_ID,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      'text-font': ['Noto Sans Regular'],
      'text-size': 13,
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: { 'text-color': labelColor, 'text-opacity': 0 },
  });

  // Single-plant marker. Fill colour follows the AlarmLevel so a red dot
  // stands out against neutral neighbours. Radius grows a hair for
  // Critical / Alarm so density on a busy region stays scannable.
  map.addLayer({
    id: LAYER_UNCLUSTERED,
    type: 'circle',
    source: SOURCE_ID,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': [
        'match',
        ['get', 'alarm'],
        'Critical', errorColor,
        'Alarm', errorColor,
        'Warning', warnColor,
        accent,
      ],
      // Trefferflaeche in der Groesse des HTML-Symbols (40 px).
      'circle-radius': 20,
      'circle-stroke-width': 0,
      'circle-stroke-color': ringColor,
      'circle-opacity': 0,
    },
  });
}

const EMPTY_SPEC: MapFilterSpec = {
  minElevationM: null,
  maxElevationM: null,
  regionIso: null,
  countryIso2: null,
  modeIn24h: null,
  minHeatingMinutes24h: null,
  onlyWithActiveAlarms: null,
  maxOfflineFor: null,
  assetGroupIds: null,
  maxNightMinC: null,
  nightIndex: null,
  states: null,
  equipment: null,
  onlyServiceActive: null,
  deviceKinds: null,
  nameContains: null,
};

/** Bundeslaender und Kantone wie in dev/fixtures/admin-boundaries (regionIso). */
const REGIONS: Record<string, [string, string][]> = {
  AT: [['AT-1', 'Burgenland'], ['AT-2', 'Kärnten'], ['AT-3', 'Niederösterreich'], ['AT-4', 'Oberösterreich'],
    ['AT-5', 'Salzburg'], ['AT-6', 'Steiermark'], ['AT-7', 'Tirol'], ['AT-8', 'Vorarlberg'], ['AT-9', 'Wien']],
  DE: [['DE-BW', 'Baden-Württemberg'], ['DE-BY', 'Bayern'], ['DE-BE', 'Berlin'], ['DE-BB', 'Brandenburg'],
    ['DE-HB', 'Bremen'], ['DE-HH', 'Hamburg'], ['DE-HE', 'Hessen'], ['DE-MV', 'Mecklenburg-Vorpommern'],
    ['DE-NI', 'Niedersachsen'], ['DE-NW', 'Nordrhein-Westfalen'], ['DE-RP', 'Rheinland-Pfalz'], ['DE-SL', 'Saarland'],
    ['DE-SN', 'Sachsen'], ['DE-ST', 'Sachsen-Anhalt'], ['DE-SH', 'Schleswig-Holstein'], ['DE-TH', 'Thüringen']],
  CH: [['CH-AG', 'Aargau'], ['CH-AI', 'Appenzell I. Rh.'], ['CH-AR', 'Appenzell A. Rh.'], ['CH-BE', 'Bern'],
    ['CH-BL', 'Basel-Landschaft'], ['CH-BS', 'Basel-Stadt'], ['CH-FR', 'Freiburg'], ['CH-GE', 'Genf'],
    ['CH-GL', 'Glarus'], ['CH-GR', 'Graubünden'], ['CH-JU', 'Jura'], ['CH-LU', 'Luzern'], ['CH-NE', 'Neuenburg'],
    ['CH-NW', 'Nidwalden'], ['CH-OW', 'Obwalden'], ['CH-SG', 'St. Gallen'], ['CH-SH', 'Schaffhausen'],
    ['CH-SO', 'Solothurn'], ['CH-SZ', 'Schwyz'], ['CH-TG', 'Thurgau'], ['CH-TI', 'Tessin'], ['CH-UR', 'Uri'],
    ['CH-VD', 'Waadt'], ['CH-VS', 'Wallis'], ['CH-ZG', 'Zug'], ['CH-ZH', 'Zürich']],
};

const STATE_CHIPS: [PlantState, string][] = [
  ['alarm', 'Alarm'], ['warn', 'Warnung / veraltet'], ['offline', 'Offline'], ['ok', 'In Ordnung'],
];

function deviceKindLabel(k: string | null): string {
  switch (k) {
    case 'panel': return 'Touch-Panel';
    case 'gateway': return 'Gateway';
    default: return 'unbekannt';
  }
}

function osKindLabel(k: string | null): string {
  switch (k) {
    case 'android': return 'Android';
    case 'linux': return 'Linux';
    case 'windows': return 'Windows';
    default: return 'unbekannt';
  }
}

/**
 * Small SVG-string helpers for the map popup. We can't render Preact into
 * a MapLibre popup body (setHTML takes a string), so the same shapes we
 * draw with the DeviceIcons.tsx components are inlined here as raw SVG.
 * `currentColor` inherits the popup's --text-primary / --text-muted so
 * both themes are legible.
 */
const SVG_ATTR = 'width="16" height="16" viewBox="0 0 20 20"';

function deviceKindSvg(k: string | null): string {
  if (k === 'panel') {
    return `<svg ${SVG_ATTR} class="hv-map__popup-glyph" aria-hidden="true">`
      + `<rect x="3.5" y="2.5" width="13" height="15" rx="1.5" stroke="currentColor" stroke-width="1.4" fill="none" />`
      + `<rect x="5" y="4" width="10" height="10" fill="currentColor" opacity="0.15" />`
      + `<circle cx="10" cy="16" r="0.8" fill="currentColor" />`
      + `</svg>`;
  }
  if (k === 'gateway') {
    return `<svg ${SVG_ATTR} class="hv-map__popup-glyph" aria-hidden="true">`
      + `<rect x="2.5" y="6" width="15" height="8" rx="1" stroke="currentColor" stroke-width="1.4" fill="none" />`
      + `<circle cx="5.5" cy="10" r="0.9" fill="currentColor" />`
      + `<circle cx="8.5" cy="10" r="0.9" fill="currentColor" />`
      + `<path d="M12 10h3.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />`
      + `<path d="M5 3.5v2M15 3.5v2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />`
      + `</svg>`;
  }
  return `<svg ${SVG_ATTR} class="hv-map__popup-glyph hv-map__popup-glyph--muted" aria-hidden="true">`
    + `<circle cx="10" cy="10" r="6.5" stroke="currentColor" stroke-width="1.2" stroke-dasharray="2 2" fill="none" />`
    + `</svg>`;
}

// Brand-logo SVG paths for the map popup — SimpleIcons `android`, hand-simplified
// `linux` tux silhouette, and SimpleIcons `windows`. Rendered at viewBox 24x24 so
// the paths line up with DeviceIcons.tsx one-to-one.
const SVG_ATTR_24 = 'width="16" height="16" viewBox="0 0 24 24"';

function osSvg(k: string | null): string {
  if (k === 'android') {
    return `<svg ${SVG_ATTR_24} class="hv-map__popup-glyph" aria-hidden="true">`
      + `<path fill="currentColor" d="M18.4395 5.5586c-.675 1.1664-1.352 2.3318-2.0274 3.498-.0366-.0155-.0742-.0286-.1113-.043-1.8249-.6957-3.484-.8-4.42-.787-1.8551.0185-3.3544.4643-4.2597.8203-.084-.1494-1.7526-3.021-2.0215-3.4864a1.1451 1.1451 0 0 0-.1406-.1914c-.3312-.364-.9054-.4859-1.379-.203-.475.282-.7136.9361-.3886 1.5019 1.9466 3.3696-.0966-.2158 1.9473 3.3593.0172.031-.4946.2642-1.3926 1.0177C2.8987 12.176.452 14.772 0 18.9902h24c-.119-1.1108-.3686-2.099-.7461-3.0683-.7438-1.9118-1.8435-3.2928-2.7402-4.1836a12.1048 12.1048 0 0 0-2.1309-1.6875c.6594-1.122 1.312-2.2559 1.9649-3.3848.2077-.3615.1886-.7956-.0079-1.1191a1.1001 1.1001 0 0 0-.8515-.5332c-.5225-.0536-.9392.3128-1.0488.5449zm-.0391 8.461c.3944.5926.324 1.3306-.1563 1.6503-.4799.3197-1.188.0985-1.582-.4941-.3944-.5927-.324-1.3307.1563-1.6504.4727-.315 1.1812-.1086 1.582.4941zM7.207 13.5273c.4803.3197.5506 1.0577.1563 1.6504-.394.5926-1.1038.8138-1.584.4941-.48-.3197-.5503-1.0577-.1563-1.6504.4008-.6021 1.1087-.8106 1.584-.4941z" />`
      + `</svg>`;
  }
  if (k === 'linux') {
    return `<svg ${SVG_ATTR_24} class="hv-map__popup-glyph" aria-hidden="true">`
      + `<path fill="currentColor" d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.132 1.884 1.071.771-.06 1.592-.536 2.257-1.306.631-.765 1.683-1.084 2.378-1.503.348-.199.629-.469.649-.853.023-.4-.2-.811-.714-1.376v-.097l-.003-.003c-.17-.2-.25-.535-.338-.926-.085-.401-.182-.786-.492-1.046h-.003c-.059-.054-.123-.067-.188-.135a.357.357 0 00-.19-.064c.431-1.278.264-2.55-.173-3.694-.533-1.41-1.465-2.638-2.175-3.483-.796-1.005-1.576-1.957-1.56-3.368.026-2.152.236-6.133-3.544-6.139zm.529 3.405h.013c.213 0 .396.062.584.198.19.135.33.332.438.533.105.259.158.459.166.724 0-.02.006-.04.006-.06v.105a.086.086 0 01-.004-.021l-.004-.024a1.807 1.807 0 01-.15.706.953.953 0 01-.213.335.71.71 0 00-.088-.042c-.104-.045-.198-.064-.284-.133a1.312 1.312 0 00-.22-.066c.05-.06.146-.133.183-.198.053-.128.082-.264.088-.402v-.02a1.21 1.21 0 00-.061-.4c-.045-.134-.101-.2-.183-.333-.084-.066-.167-.132-.267-.132h-.016c-.093 0-.176.03-.262.132a.8.8 0 00-.205.334 1.18 1.18 0 00-.09.4v.019c.002.089.008.179.02.267-.193-.067-.438-.135-.607-.202a1.635 1.635 0 01-.018-.2v-.02a1.772 1.772 0 01.15-.768c.082-.22.232-.406.43-.533a.985.985 0 01.594-.2zm-2.962.059h.036c.142 0 .27.048.399.135.146.129.264.288.344.465.09.199.14.4.153.667v.004c.007.134.006.2-.002.266v.08c-.03.007-.056.018-.083.024-.152.055-.274.135-.393.2.012-.09.013-.18.003-.267v-.015c-.012-.133-.04-.2-.082-.333a.613.613 0 00-.166-.267.248.248 0 00-.183-.064h-.021c-.071.006-.13.04-.186.132a.552.552 0 00-.12.27.944.944 0 00-.023.33v.015c.012.135.037.2.08.334.046.134.098.2.166.268.01.009.02.018.034.024-.07.057-.117.07-.176.136a.304.304 0 01-.131.068 2.62 2.62 0 01-.275-.402 1.772 1.772 0 01-.155-.667 1.759 1.759 0 01.08-.668 1.43 1.43 0 01.283-.535c.128-.133.26-.2.418-.2zm1.37 1.706c.332 0 .733.065 1.216.399.293.2.523.269 1.052.468h.003c.255.136.405.266.478.399v-.131a.571.571 0 01.016.47c-.123.31-.516.643-1.063.842v.002c-.268.135-.501.333-.775.465-.276.135-.588.292-1.012.267a1.139 1.139 0 01-.448-.067 3.566 3.566 0 01-.322-.198c-.195-.135-.363-.332-.612-.465v-.005h-.005c-.4-.246-.616-.512-.686-.71-.07-.268-.005-.47.193-.6.224-.135.38-.271.483-.336.104-.074.143-.102.176-.131h.002v-.003c.169-.202.436-.47.839-.601.139-.036.294-.065.466-.065zm2.8 2.142c.358 1.417 1.196 3.475 1.735 4.473.286.534.855 1.659 1.102 3.024.156-.005.33.018.513.064.646-1.671-.546-3.467-1.089-3.966-.22-.2-.232-.335-.123-.335.59.534 1.365 1.572 1.646 2.757.13.535.16 1.104.021 1.67.067.028.135.06.205.067 1.032.534 1.413.938 1.23 1.537v-.043c-.06-.003-.12 0-.18 0h-.016c.151-.467-.182-.825-1.065-1.224-.915-.4-1.646-.336-1.77.465-.008.043-.013.066-.018.135-.068.023-.139.053-.209.064-.43.268-.662.669-.793 1.187-.13.533-.17 1.156-.205 1.869v.003c-.02.334-.17.838-.319 1.35-1.5 1.072-3.58 1.538-5.348.334a2.645 2.645 0 00-.402-.533 1.45 1.45 0 00-.275-.333c.182 0 .338-.03.465-.067a.615.615 0 00.314-.334c.108-.267 0-.697-.345-1.163-.345-.467-.931-.995-1.788-1.521-.63-.4-.986-.87-1.15-1.396-.165-.534-.143-1.085-.015-1.645.245-1.07.873-2.11 1.274-2.763.107-.065.037.135-.408.974-.396.751-1.14 2.497-.122 3.854a8.123 8.123 0 01.647-2.876c.564-1.278 1.743-3.504 1.836-5.268.048.036.217.135.289.202.218.133.38.333.59.465.21.201.477.335.876.335.039.003.075.006.11.006.412 0 .73-.134.997-.268.29-.134.52-.334.74-.4h.005c.467-.135.835-.402 1.044-.7zm2.185 8.958c.037.6.343 1.245.882 1.377.588.134 1.434-.333 1.791-.765l.211-.01c.315-.007.577.01.847.268l.003.003c.208.199.305.53.391.876.085.4.154.78.409 1.066.486.527.645.906.636 1.14l.003-.007v.018l-.003-.012c-.015.262-.185.396-.498.595-.63.401-1.746.712-2.457 1.57-.618.737-1.37 1.14-2.036 1.191-.664.053-1.237-.2-1.574-.898l-.005-.003c-.21-.4-.12-1.025.056-1.69.176-.668.428-1.344.463-1.897.037-.714.076-1.335.195-1.814.12-.465.308-.797.641-.984l.045-.022zm-10.814.049h.01c.053 0 .105.005.157.014.376.055.706.333 1.023.752l.91 1.664.003.003c.243.533.754 1.064 1.189 1.637.434.598.77 1.131.729 1.57v.006c-.057.744-.48 1.148-1.125 1.294-.645.135-1.52.002-2.395-.464-.968-.536-2.118-.469-2.857-.602-.369-.066-.61-.2-.723-.4-.11-.2-.113-.602.123-1.23v-.004l.002-.003c.117-.334.03-.752-.027-1.118-.055-.401-.083-.71.043-.94.16-.334.396-.4.69-.533.294-.135.64-.202.915-.47h.002v-.002c.256-.268.445-.601.668-.838.19-.201.38-.336.663-.336zm7.159-9.074c-.435.201-.945.535-1.488.535-.542 0-.97-.267-1.28-.466-.154-.134-.28-.268-.373-.335-.164-.134-.144-.333-.074-.333.109.016.129.134.199.2.096.066.215.2.36.333.292.2.68.467 1.167.467.485 0 1.053-.267 1.398-.466.195-.135.445-.334.648-.467.156-.136.149-.267.279-.267.128.016.034.134-.147.332a8.097 8.097 0 01-.69.468zm-1.082-1.583V5.64c-.006-.02.013-.042.029-.05.074-.043.18-.027.26.004.063 0 .16.067.15.135-.006.049-.085.066-.135.066-.055 0-.092-.043-.141-.068-.052-.018-.146-.008-.163-.065zm-.551 0c-.02.058-.113.049-.166.066-.047.025-.086.068-.14.068-.05 0-.13-.02-.136-.068-.01-.066.088-.133.15-.133.08-.031.184-.047.259-.005.019.009.036.03.03.05v.02h.003z" />`
      + `</svg>`;
  }
  if (k === 'windows') {
    return `<svg ${SVG_ATTR_24} class="hv-map__popup-glyph" aria-hidden="true">`
      + `<path fill="currentColor" d="M0,0H11.377V11.372H0ZM12.623,0H24V11.372H12.623ZM0,12.623H11.377V24H0Zm12.623,0H24V24H12.623" />`
      + `</svg>`;
  }
  return `<svg ${SVG_ATTR} class="hv-map__popup-glyph hv-map__popup-glyph--muted" aria-hidden="true">`
    + `<circle cx="10" cy="10" r="6.5" stroke="currentColor" stroke-width="1.2" stroke-dasharray="2 2" fill="none" />`
    + `</svg>`;
}

function alarmLabel(a: AlarmLevel): string {
  switch (a) {
    case 'Critical': return 'Kritisch';
    case 'Alarm': return 'Alarm';
    case 'Warning': return 'Warnung';
    case 'None': return 'Keine';
    default: return a;
  }
}

/**
 * Parse a possibly persisted SavedMapView.SpecJson back into a MapFilterSpec.
 * The server serialises the C# record with camelCase names; empty payloads
 * fall back to EMPTY_SPEC so a bad row cannot wedge the UI.
 */
function parseSpec(specJson: string): MapFilterSpec {
  try {
    const raw = JSON.parse(specJson);
    if (raw && typeof raw === 'object') {
      const spec: MapFilterSpec = { ...EMPTY_SPEC, ...raw };
      if (spec.onlyWithActiveAlarms && !spec.states?.length) spec.states = ['alarm'];
      spec.onlyWithActiveAlarms = null;
      spec.maxOfflineFor = null;
      return spec;
    }
  } catch { /* fall through */ }
  return EMPTY_SPEC;
}

/**
 * Full-height, theme-reactive map of plants. Fetches from POST /api/map/assets
 * so the filter panel can narrow by elevation, region, alarm state and mode.
 * Empty filter = all plants (equivalent to the old GET /api/plants call).
 */
export function MapPage() {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const styleReadyRef = useRef<boolean>(false);
  const popupRef = useRef<maplibregl.Popup | null>(null);

  const [assets, setAssets] = useState<MapAssetDto[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [filter, setFilter] = useState<MapFilterSpec>(EMPTY_SPEC);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [views, setViews] = useState<SavedMapViewDto[]>([]);
  const [saveModalOpen, setSaveModalOpen] = useState<boolean>(false);
  const [mapView, setMapView] = useState<MapViewSettings>(readMapView);
  // Ansicht folgt dem Benutzer (Patrick, 23.10.2026): beim Oeffnen vom
  // Server holen, Aenderungen dorthin zurueckschreiben. Der Browser-Speicher
  // bleibt als schneller erster Stand und fuer den Fall ohne Server.
  const prefLoadedRef = useRef<boolean>(false);
  const prefSavedRef = useRef<string>('');
  useEffect(() => {
    const controller = new AbortController();
    api.get<Partial<MapViewSettings> | undefined>(`/api/me/preferences/${MAP_VIEW_PREF}`, controller.signal)
      .then((stored) => {
        if (controller.signal.aborted) return;
        prefLoadedRef.current = true;
        if (stored && typeof stored === 'object') {
          const v = normalizeMapView(stored);
          prefSavedRef.current = JSON.stringify(v);
          setMapView(v);
        }
        // Ohne Eintrag am Server: der naechste Effektlauf schreibt den
        // bisherigen Browser-Stand hinauf, damit er nicht verloren geht.
        else setMapView((v) => ({ ...v }));
      })
      .catch(() => { prefLoadedRef.current = true; });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (!prefLoadedRef.current) return;
    const json = JSON.stringify(mapView);
    if (json === prefSavedRef.current) return;
    const t = window.setTimeout(() => {
      api.put(`/api/me/preferences/${MAP_VIEW_PREF}`, mapView)
        .then(() => { prefSavedRef.current = json; })
        .catch(() => { /* bleibt im Browser, naechste Aenderung versucht es wieder */ });
    }, 600);
    return () => window.clearTimeout(t);
  }, [mapView]);
  const [viewOpen, setViewOpen] = useState<boolean>(false);
  const [christlOpen, setChristlOpen] = useState<boolean>(false);
  const mapViewRef = useRef<MapViewSettings>(mapView);
  mapViewRef.current = mapView;
  const filterRef = useRef<MapFilterSpec>(filter);
  filterRef.current = filter;
  // Grenzen einmal je Sitzung laden; ohne PostGIS kommt eine leere Sammlung.
  const boundariesRef = useRef<{ countries: FeatureCollection | null; regions: FeatureCollection; nuts3: FeatureCollection | null }>({ countries: null, regions: EMPTY_FC, nuts3: null });
  const elevActive = hasElevationFilter(filter);
  const [weather, setWeather] = useState<MapWeatherDto | null>(null);
  const weatherRef = useRef<MapWeatherDto | null>(null);
  weatherRef.current = weather;
  const weatherOn = mapView.weather || filter.maxNightMinC != null;
  // Flaechen- und Symbolquellen aus Grenzen, Wetter, Ansicht und Filter.
  const applyWeather = () => {
    const map = mapRef.current;
    if (!map) return;
    const sets = weatherSets(boundariesRef.current, weatherRef.current,
      { ...mapViewRef.current, weather: mapViewRef.current.weather || filterRef.current.maxNightMinC != null }, filterRef.current);
    WX_LEVELS.forEach((lvl, i) => {
      (map.getSource(lvl.id) as GeoJSONSource | undefined)?.setData(sets.fills[i]);
      (map.getSource(lvl.pt) as GeoJSONSource | undefined)?.setData(mapViewRef.current.wxIcons ? sets.points[i] : EMPTY_FC);
    });
  };

  const assetsRef = useRef<MapAssetDto[] | null>(null);
  assetsRef.current = assets;

  // Fetch assets whenever the filter changes. Debounced 300ms so a slider
  // drag doesn't hammer the server with intermediate values.
  useEffect(() => {
    const controller = new AbortController();
    const t = window.setTimeout(() => {
      api.post<MapAssetDto[]>('/api/map/assets', filter, controller.signal)
        .then((r) => {
          if (!controller.signal.aborted) setAssets(r);
        })
        .catch((e) => {
          if (!controller.signal.aborted) setError(e);
        });
    }, 300);
    return () => {
      controller.abort();
      window.clearTimeout(t);
    };
  }, [filter]);

  // Load saved views once. The dropdown reloads whenever we mutate through
  // save/delete via the local list state (no need to hit the server twice).
  useEffect(() => {
    const controller = new AbortController();
    api.get<SavedMapViewDto[]>('/api/map/views', controller.signal)
      .then((r) => { if (!controller.signal.aborted) setViews(r); })
      .catch(() => { /* ignore: SavedViews are a niceness, not a blocker */ });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;

    const map = new maplibregl.Map({
      container: el,
      style: buildMapStyle(themeSignal.value),
      center: EUROPE_CENTER,
      zoom: EUROPE_ZOOM,
      // Attribution-Control ganz aus (Pazi 2026-08-29). Wenn ihr die
      // Karten spaeter oeffentlich zugaenglich macht, MUSS ein
      // OSM+OpenMapTiles-Nachweis irgendwo im Impressum/Footer stehen —
      // aktuell reines Internal-Tool.
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');

    // Suppress MapLibre's "Image X could not be loaded" warnings for POI
    // icons that the vector-tile style references but our sprite sheet does
    // not carry (townhall, fire_station, etc.). We do not display these POIs
    // anyway; the style layer just tries to resolve their icon-image at paint
    // time. Providing a 1x1 transparent bitmap short-circuits the resolution
    // and keeps the console clean. See MapLibre docs: styleimagemissing event.
    map.on('styleimagemissing', (e: { id: string }) => {
      if (map.hasImage(e.id)) return;
      map.addImage(e.id, { width: 1, height: 1, data: new Uint8Array(4) });
    });

    const onStyleLoad = () => {
      styleReadyRef.current = true;
      const accent = readCssVar('--accent', '#3b82f6');
      const ring = readCssVar('--surface-panel', '#ffffff');
      const label = readCssVar('--surface-base', '#ffffff');
      const warn = readCssVar('--status-warn', '#f59e0b');
      const err = readCssVar('--status-error', '#ef4444');
      const data = toFeatureCollection(assetsRef.current ?? []);
      const b = boundariesRef.current;
      installBoundaryLayers(map, b.regions, b.nuts3 ?? EMPTY_FC, elevationSets(b, filterRef.current));
      applyBoundaryView(map, mapViewRef.current.borders, filterRef.current);
      installPlantsLayers(map, data, accent, ring, label, warn, err, mapViewRef.current.cluster);
      // Wettersymbole ueber allem ausser den Anlagen (HTML-Marker); die
      // Bilder gehen bei jedem Stilwechsel verloren und kommen neu.
      applyOverlayStyle(map, mapViewRef.current,
        mapViewRef.current.weather || filterRef.current.maxNightMinC != null);
      void addWeatherImages(map).then(() => {
        installWeatherSymbols(map);
        applyWeather();
      });
    };
    map.on('load', onStyleLoad);
    map.on('styledata', onStyleLoad);

    // HTML-Symbole bei jedem Zeichnen mit den sichtbaren Punkten und
    // Clustern abgleichen (Muster aus den MapLibre-Beispielen).
    const markers = new Map<string, maplibregl.Marker>();
    const shown = new Set<string>();
    const syncMarkers = () => {
      if (!map.getSource(SOURCE_ID) || !map.isSourceLoaded(SOURCE_ID)) return;
      const next = new Set<string>();
      // Anlagen, die auf dem Bildschirm aufeinander laegen (gleicher Standort,
      // etwa mehrere Regler im Labor), im Kreis um ihren Punkt auffaechern.
      const features = map.querySourceFeatures(SOURCE_ID);
      const fan = new Map<string, [number, number]>();
      const buckets = new Map<string, string[]>();
      const seenIds = new Set<string>();
      for (const f of features) {
        const props = f.properties as Record<string, unknown>;
        if (props.point_count !== undefined || seenIds.has(String(props.id))) continue;
        seenIds.add(String(props.id));
        const px = map.project((f.geometry as Point).coordinates as [number, number]);
        const cell = `${Math.round(px.x / FAN_CELL_PX)}:${Math.round(px.y / FAN_CELL_PX)}`;
        const list = buckets.get(cell) ?? [];
        list.push(String(props.id));
        buckets.set(cell, list);
      }
      for (const ids of buckets.values()) {
        if (ids.length < 2) continue;
        ids.sort();
        const r = FAN_RADIUS_PX + Math.max(0, ids.length - 4) * 4;
        ids.forEach((id, i) => {
          const a = -Math.PI / 2 + (2 * Math.PI * i) / ids.length;
          fan.set(id, [Math.round(Math.cos(a) * r), Math.round(Math.sin(a) * r)]);
        });
      }
      for (const f of features) {
        const props = f.properties as Record<string, unknown>;
        const coords = (f.geometry as Point).coordinates as [number, number];
        const isCluster = props.cluster === true || props.point_count !== undefined;
        const key = isCluster
          ? `c${props.cluster_id}:${props.point_count}:${props.nAlarm}:${props.nWarn}:${props.nOffline}`
          : `p${props.id}:${props.state}:${props.main}:${props.subs}:${props.more}:${props.alarmCount}:${props.service}`;
        if (next.has(key)) continue;
        next.add(key);
        let m = markers.get(key);
        if (!m) {
          const el = document.createElement('div');
          if (isCluster) {
            el.className = 'hv-mk-cl';
            el.innerHTML = clusterSvg(Number(props.point_count), Number(props.nAlarm ?? 0),
              Number(props.nWarn ?? 0), Number(props.nOffline ?? 0));
          } else {
            el.className = `hv-mk hv-mk--${String(props.state)}`;
            const plantProps = props as unknown as PlantFeature['properties'];
            const plantCoords = coords;
            el.addEventListener('click', (ev) => {
              ev.stopPropagation();
              openPlantPopup(plantProps, plantCoords);
            });
            el.innerHTML = markerHtml({
              state: props.state as PlantState,
              main: String(props.main),
              subs: String(props.subs ?? ''),
              more: Number(props.more ?? 0),
              alarmCount: Number(props.alarmCount ?? 0),
              service: props.service === true || props.service === 'true',
            } satisfies MarkerProps);
          }
          m = new maplibregl.Marker({ element: el }).setLngLat(coords);
          markers.set(key, m);
        }
        if (!isCluster) {
          const off = fan.get(String(props.id)) ?? [0, 0];
          const cur = m.getOffset();
          if (cur.x !== off[0] || cur.y !== off[1]) m.setOffset(off);
          m.getElement().classList.toggle('hv-mk--fanned', fan.has(String(props.id)));
        }
        if (!shown.has(key)) m.addTo(map);
      }
      for (const key of shown) if (!next.has(key)) markers.get(key)?.remove();
      shown.clear();
      for (const key of next) shown.add(key);
    };
    map.on('render', syncMarkers);

    map.on('click', LAYER_CLUSTERS, (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
      const feature = e.features?.[0];
      if (!feature) return;
      const clusterId = feature.properties?.cluster_id as number | undefined;
      if (clusterId === undefined) return;
      const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
      if (!source) return;
      source.getClusterExpansionZoom(clusterId).then((zoom) => {
        const geom = feature.geometry as Point;
        map.easeTo({ center: geom.coordinates as [number, number], zoom });
      }).catch(() => { /* cluster gone */ });
    });

    // Point click: popup with plant details + link to plant schema page.
    // Aufgerufen vom Klick auf das Anlagensymbol (syncMarkers), weil
    // aufgefaecherte Symbole nicht mehr ueber ihrem Punkt der Ebene liegen.
    function openPlantPopup(props: PlantFeature['properties'], coords: [number, number]) {
      const elevation = typeof props.elevationM === 'number' ? `${Math.round(props.elevationM)} m` : '—';
      const alarmText = alarmLabel(props.alarm as AlarmLevel);
      const onlineText = props.online ? 'online' : 'offline';
      const deviceGlyph = deviceKindSvg(props.deviceKind);
      const deviceLabel = deviceKindLabel(props.deviceKind);
      const osGlyph = osSvg(props.osKind);
      const osLabel = osKindLabel(props.osKind);
      const html =
        `<div class="hv-map__popup">
          <div class="hv-map__popup-title">${escapeHtml(props.displayName)}</div>
          <dl class="hv-map__popup-meta">
            <dt>Client</dt><dd class="hv-map__popup-glyphline">${deviceGlyph}<span>${escapeHtml(deviceLabel)}</span></dd>
            <dt>OS</dt><dd class="hv-map__popup-glyphline">${osGlyph}<span>${escapeHtml(osLabel)}</span></dd>
            <dt>Höhe</dt><dd>${elevation}</dd>
            <dt>Zustand</dt><dd class="hv-map__popup-state hv-map__popup-state--${props.state}">${escapeHtml(STATE_LABEL[props.state as PlantState] ?? onlineText)}${props.alarmCount > 0 ? ` · ${props.alarmCount} ${props.alarmCount === 1 ? 'Alarm' : 'Alarme'}` : ''}</dd>
            <dt>Alarm</dt><dd class="hv-map__popup-alarm hv-map__popup-alarm--${props.alarm}">${escapeHtml(alarmText)}</dd>
            <dt>Geräte</dt><dd>${escapeHtml(props.equipment)}</dd>${props.service ? '<dt>Service</dt><dd>Servicezugang offen</dd>' : ''}
            ${props.weatherHtml ?? ''}
          </dl>
          <a class="hv-map__popup-link" href="/plants/${encodeURIComponent(props.id)}/schema" data-nav>Anlage öffnen →</a>
        </div>`;
      popupRef.current?.remove();
      popupRef.current = new maplibregl.Popup({ closeButton: true, offset: 12 })
        .setLngLat(coords)
        .setHTML(html)
        .addTo(map);
      // Delegate SPA nav so the link doesn't do a full reload.
      const el = popupRef.current.getElement();
      const linkEl = el.querySelector<HTMLAnchorElement>('a[data-nav]');
      if (linkEl) linkEl.onclick = (ev) => {
        ev.preventDefault();
        popupRef.current?.remove();
        location.route(`/plants/${props.id}/schema`);
      };
    }

    // Klick auf eine eingefaerbte Flaeche: Name und Hoehenanteil.
    for (const lvl of ELEV_LEVELS) {
      map.on('click', `${lvl.id}-fill`, (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
        if (e.defaultPrevented) return;
        const p = e.features?.[0]?.properties as Record<string, unknown> | undefined;
        if (!p) return;
        const f = filterRef.current;
        const band = f.minElevationM != null && f.maxElevationM != null
          ? `zwischen ${f.minElevationM} und ${f.maxElevationM} m`
          : f.minElevationM != null ? `über ${f.minElevationM} m` : `unter ${f.maxElevationM} m`;
        popupRef.current?.remove();
        popupRef.current = new maplibregl.Popup({ closeButton: true })
          .setLngLat(e.lngLat)
          .setHTML(`<div class="hv-map__popup">
              <div class="hv-map__popup-title">${escapeHtml(String(p.name ?? ''))}</div>
              <dl class="hv-map__popup-meta">
                <dt>Fläche</dt><dd>${Math.round(Number(p.share) * 100)} % ${escapeHtml(band)}</dd>
                <dt>Höhe</dt><dd>${p.elevMin} bis ${p.elevMax} m, im Mittel ${p.elevMean} m</dd>
              </dl>
            </div>`)
          .addTo(map);
      });
    }

    for (const layerId of [LAYER_CLUSTERS]) {
      map.on('mouseenter', layerId, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', layerId, () => { map.getCanvas().style.cursor = ''; });
    }

    return () => {
      map.remove();
      mapRef.current = null;
      styleReadyRef.current = false;
      popupRef.current?.remove();
      popupRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Avoid the "Unable to perform style diff: Style is not done loading"
    // warning by waiting for the current style to finish before swapping.
    // Called with a stale styleReadyRef when the map is still on its initial
    // load — in that case the target style is already the current one, so
    // there is nothing to swap.
    const swap = () => {
      styleReadyRef.current = false;
      map.setStyle(buildMapStyle(themeSignal.value));
    };
    if (map.isStyleLoaded()) {
      swap();
    } else {
      map.once('idle', swap);
    }
  }, [themeSignal.value]);

  // Bundeslaender sofort laden (Hervorhebung im Filter), NUTS-3 erst, wenn
  // diese Linien eingeschaltet werden oder ein Hoehenfilter greift, Laender
  // nur fuer den Hoehenfilter.
  const [boundaryRev, setBoundaryRev] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    const b = boundariesRef.current;
    const want: ('countries' | 'regions' | 'nuts3')[] = b.regions.features.length === 0 ? ['regions'] : [];
    if ((mapView.borders === 'nuts3' || elevActive || weatherOn) && b.nuts3 === null) want.push('nuts3');
    if ((elevActive || weatherOn) && b.countries === null) want.push('countries');
    for (const kind of want) {
      const precision = kind === 'countries' ? 0 : kind === 'regions' ? 1 : 2;
      api.get<FeatureCollection>(`/api/map/boundaries?precision=${precision}`, controller.signal)
        .then((fc) => {
          if (controller.signal.aborted) return;
          b[kind] = fc;
          const src = kind === 'countries' ? undefined
            : mapRef.current?.getSource(kind === 'regions' ? REGIONS_SOURCE : NUTS3_SOURCE) as GeoJSONSource | undefined;
          src?.setData(fc);
          setBoundaryRev((r) => r + 1);
        })
        .catch(() => { /* ohne Grenzen geht die Karte trotzdem */ });
    }
    return () => controller.abort();
  }, [mapView.borders, elevActive, weatherOn]);

  // Wetter je Flaeche laden, solange die Ebene an ist; alle 10 Minuten neu
  // (der Server erneuert stuendlich).
  useEffect(() => {
    if (!weatherOn) return;
    const controller = new AbortController();
    const load = () => api.get<MapWeatherDto>('/api/map/weather', controller.signal)
      .then((w) => { if (!controller.signal.aborted) setWeather(w); })
      .catch(() => { /* Karte geht ohne Wetter */ });
    void load();
    const t = window.setInterval(() => void load(), 10 * 60 * 1000);
    return () => { controller.abort(); window.clearInterval(t); };
  }, [weatherOn]);

  useEffect(() => {
    applyWeather();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weather, mapView, filter.maxNightMinC, filter.nightIndex, boundaryRev]);

  // Hoehenbaender nachfuehren, wenn Filter oder Grenzdaten sich aendern.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const sets = elevationSets(boundariesRef.current, filter);
    ELEV_LEVELS.forEach((lvl, i) => (map.getSource(lvl.id) as GeoJSONSource | undefined)?.setData(sets[i]));
  }, [filter.minElevationM, filter.maxElevationM, boundaryRev]);

  useEffect(() => {
    writeMapView(mapView);
    const map = mapRef.current;
    if (!map) return;
    applyBoundaryView(map, mapView.borders, filter);
    applyOverlayStyle(map, mapView, mapView.weather || filter.maxNightMinC != null);
    const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    source?.setClusterOptions(clusterOptions(mapView.cluster));
  }, [mapView, filter]);

  // Push new assets into the layer + fit bounds. When the filter narrows
  // to a small subset we zoom in on it; when it widens back to all plants
  // we stay wherever the operator navigated to.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !assets) return;

    const data = toFeatureCollection(assets);
    const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (source) source.setData(data);

    // Only auto-fit when the filter is non-empty AND the result is a small
    // enough set to make zooming meaningful. Otherwise keep the current view.
    if (!hasActiveFilter(filter)) return;
    const withCoords = data.features;
    if (withCoords.length === 0) return;
    if (withCoords.length === 1) {
      const coords = withCoords[0].geometry.coordinates as [number, number];
      map.jumpTo({ center: coords, zoom: MAX_FIT_ZOOM });
      return;
    }
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    for (const f of withCoords) {
      const [lng, lat] = f.geometry.coordinates as [number, number];
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    }
    const bounds: LngLatBoundsLike = [[minLng, minLat], [maxLng, maxLat]];
    map.fitBounds(bounds, { padding: 48, maxZoom: MAX_FIT_ZOOM, duration: 400 });
  }, [assets, filter]);

  // Deep-link Focus: `?plantId=X` in der URL springt beim ersten Assets-Fetch
  // auf die Anlage und oeffnet den Detail-Popup. Der Ref-Guard verhindert
  // dass ein zweiter Fetch die View des Operators wieder zurueckreisst.
  const focusedPlantIdRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !assets) return;
    const rawQuery = (location.query as Record<string, string> | undefined) ?? {};
    const wanted = rawQuery.plantId;
    if (!wanted || focusedPlantIdRef.current === wanted) return;
    const hit = assets.find((a) => a.id === wanted);
    if (!hit) return;
    focusedPlantIdRef.current = wanted;
    // Zoom 14 = Strassenzug-Ebene, sinnvoll fuer eine einzelne Anlage.
    // MAX_FIT_ZOOM (6) reicht nur bis Bundesland-Ebene und wuerde einen
    // Deep-Link auf "diese Anlage" wirkungslos machen.
    map.flyTo({ center: [hit.lon, hit.lat], zoom: 14, duration: 600 });
  }, [assets, location.query]);

  const onLoadView = (id: string) => {
    if (!id) { setFilter(EMPTY_SPEC); return; }
    const v = views.find((x) => x.id === id);
    if (v) setFilter(parseSpec(v.specJson));
  };

  const onSaveView = async (name: string, sharedWithScope: boolean) => {
    const req: SavedMapViewUpsertRequest = { name, spec: filter, sharedWithScope };
    try {
      const saved = await api.post<SavedMapViewDto>('/api/map/views', req);
      setViews((prev) => [...prev, saved].sort((a, b) => a.name.localeCompare(b.name)));
      showToast({ tone: 'success', title: 'Kartenansicht gespeichert', message: saved.name });
      setSaveModalOpen(false);
    } catch (e) {
      showToast({ tone: 'error', title: 'Speichern fehlgeschlagen', message: (e as Error).message });
    }
  };

  return (
    <AppShell title="Karte">
      <div class={`hv-map${filterOpen ? ' hv-map--drawer-open' : ''}`}>
        <div ref={containerRef} class="hv-map__canvas" role="region" aria-label="Anlagen-Karte" />

        {/* Top-right control strip: filter toggle + saved-view picker.
            Positioned absolutely over the canvas so it doesn't push the map. */}
        <div class="hv-map__controls">
          <select
            class="hv-map__views"
            aria-label="Gespeicherte Ansicht"
            onChange={(e) => onLoadView((e.currentTarget as HTMLSelectElement).value)}
          >
            <option value="">Alle Anlagen</option>
            {views.map((v) => (
              <option key={v.id} value={v.id}>
                {v.ownerUserId === null ? `[Team] ${v.name}` : v.name}
              </option>
            ))}
          </select>
          <div class="hv-map__view-wrap">
            <button
              type="button"
              class={`hv-map__filter-btn hv-map__christl-btn${christlOpen ? ' hv-map__christl-btn--open' : ''}`}
              onClick={() => { setChristlOpen((o) => !o); setViewOpen(false); }}
              aria-pressed={christlOpen}
              aria-expanded={christlOpen}
              title="Christl, die KI-Assistentin, stellt den Filter für eine Aufgabe zusammen"
            >
              <span aria-hidden="true" class="hv-map__christl-spark">✦</span> Christl
            </button>
            {christlOpen && (
              <ChristlPanel
                filter={filter}
                onApply={(f) => setFilter(f)}
                onShowFilter={() => { setChristlOpen(false); setFilterOpen(true); }}
                onClose={() => setChristlOpen(false)}
              />
            )}
          </div>
          <div class="hv-map__view-wrap">
            <button
              type="button"
              class="hv-map__filter-btn"
              onClick={() => { setViewOpen((o) => !o); setChristlOpen(false); }}
              aria-pressed={viewOpen}
              aria-expanded={viewOpen}
            >
              Ansicht
            </button>
            {viewOpen && (
              <div class="hv-map__view-pop" role="dialog" aria-label="Kartenansicht">
                <label class="hv-map__field-label" for="clusterRange">Anlagen zusammenfassen</label>
                <input
                  id="clusterRange"
                  type="range"
                  min="0"
                  max={CLUSTER_STEPS.length - 1}
                  step="1"
                  value={mapView.cluster}
                  onInput={(e) => setMapView({ ...mapView, cluster: Number((e.currentTarget as HTMLInputElement).value) })}
                />
                <div class="hv-map__view-scale" aria-hidden="true"><span>nie</span><span>stark</span></div>
                <div class="hv-map__view-value">{CLUSTER_STEPS[mapView.cluster].label}</div>

                <span class="hv-map__field-label">Grenzen</span>
                <div class="hv-map__chips">
                  {([['none', 'Keine'], ['regions', 'Bundesländer'], ['nuts3', 'Mit Regionen']] as [BorderMode, string][]).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      class={`hv-map__chip hv-map__chip--small${mapView.borders === key ? ' hv-map__chip--on' : ''}`}
                      onClick={() => setMapView({ ...mapView, borders: key })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <span class="hv-map__field-label">Wetter</span>
                <div class="hv-map__chips">
                  {([[false, 'Aus'], [true, 'Temperatur']] as [boolean, string][]).map(([on, label]) => (
                    <button
                      key={label}
                      type="button"
                      class={`hv-map__chip hv-map__chip--small${mapView.weather === on ? ' hv-map__chip--on' : ''}`}
                      onClick={() => setMapView({ ...mapView, weather: on })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {mapView.weather && (
                  <>
                    <select
                      class="hv-map__view-select"
                      aria-label="Wetterwert"
                      value={mapView.wxValue}
                      onChange={(e) => setMapView({ ...mapView, wxValue: (e.currentTarget as HTMLSelectElement).value as WeatherValue })}
                    >
                      {WEATHER_VALUES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                    <label class="hv-map__field-check">
                      <input
                        type="checkbox"
                        checked={mapView.wxIcons}
                        onChange={(e) => setMapView({ ...mapView, wxIcons: (e.currentTarget as HTMLInputElement).checked })}
                      />
                      Wettersymbole
                    </label>
                  </>
                )}
                <details class="hv-map__view-more">
                  <summary>Darstellung der Einfärbungen</summary>
                  <span class="hv-map__field-label">Höhe</span>
                  <SegChips
                    value={mapView.elevStyle}
                    options={[['auto', 'Automatisch'], ['fill', 'Fläche'], ['outline', 'Umrandung'], ['both', 'Beides']]}
                    onChange={(v) => setMapView({ ...mapView, elevStyle: v as ElevStyle })}
                  />
                  <SegChips
                    value={mapView.elevStrength}
                    options={[['low', 'Schwach'], ['mid', 'Mittel'], ['high', 'Kräftig']]}
                    onChange={(v) => setMapView({ ...mapView, elevStrength: v as Strength })}
                  />
                  <div class="hv-map__swatches" role="radiogroup" aria-label="Farbe der Höhe">
                    {ELEV_COLORS.map(([c, label]) => (
                      <button
                        key={c}
                        type="button"
                        role="radio"
                        aria-checked={mapView.elevColor === c}
                        title={label}
                        class={`hv-map__swatch${mapView.elevColor === c ? ' hv-map__swatch--on' : ''}`}
                        style={{ background: c }}
                        onClick={() => setMapView({ ...mapView, elevColor: c })}
                      >
                        <span class="hv-map__sr">{label}</span>
                      </button>
                    ))}
                  </div>
                  <span class="hv-map__field-label">Wetter</span>
                  <SegChips
                    value={mapView.wxStyle}
                    options={[['fill', 'Fläche'], ['outline', 'Umrandung'], ['both', 'Beides']]}
                    onChange={(v) => setMapView({ ...mapView, wxStyle: v as OverlayStyle })}
                  />
                  <SegChips
                    value={mapView.wxStrength}
                    options={[['low', 'Schwach'], ['mid', 'Mittel'], ['high', 'Kräftig']]}
                    onChange={(v) => setMapView({ ...mapView, wxStrength: v as Strength })}
                  />
                  <button
                    type="button"
                    class="hv-map__ghost hv-map__view-reset"
                    onClick={() => setMapView({
                      ...mapView,
                      elevStyle: DEFAULT_VIEW.elevStyle, elevStrength: DEFAULT_VIEW.elevStrength, elevColor: DEFAULT_VIEW.elevColor,
                      wxStyle: DEFAULT_VIEW.wxStyle, wxStrength: DEFAULT_VIEW.wxStrength,
                    })}
                  >
                    Standard wiederherstellen
                  </button>
                </details>
                <div class="hv-map__view-note">Gewählte Länder und Bundesländer im Filter werden hervorgehoben. Grenzen: © EuroGeographics</div>
              </div>
            )}
          </div>
          <button
            type="button"
            class={`hv-map__filter-btn${hasActiveFilter(filter) ? ' hv-map__filter-btn--active' : ''}`}
            onClick={() => setFilterOpen((o) => !o)}
            aria-pressed={filterOpen}
          >
            Filter{hasActiveFilter(filter) ? ` (${countActiveDims(filter)})` : ''}
          </button>
        </div>

        {filterOpen && (
          <FilterDrawer
            filter={filter}
            onChange={setFilter}
            onClose={() => setFilterOpen(false)}
            onSaveClick={() => setSaveModalOpen(true)}
            resultCount={assets?.length ?? null}
          />
        )}

        {saveModalOpen && (
          <SaveViewModal
            onSave={onSaveView}
            onClose={() => setSaveModalOpen(false)}
          />
        )}

        {elevActive && (
          <div class="hv-map__legend" aria-label="Legende Höhe">
            <div class="hv-map__legend-title">
              Flächenanteil {filter.minElevationM != null && filter.maxElevationM != null
                ? `${filter.minElevationM} bis ${filter.maxElevationM} m`
                : filter.minElevationM != null ? `über ${filter.minElevationM} m` : `unter ${filter.maxElevationM} m`}
            </div>
            <div class="hv-map__legend-ramp" style={{ background: `linear-gradient(90deg, ${mapView.elevColor}10, ${mapView.elevColor}99)` }} />
            <div class="hv-map__legend-scale"><span>0 %</span><span>100 %</span></div>
            <div class="hv-map__legend-note">Gelände: Copernicus DEM GLO-90, © DLR, Airbus DS</div>
          </div>
        )}

        {weatherOn && weather && (
          <div class={`hv-map__legend hv-map__legend--wx`} aria-label="Legende Temperatur">
            <div class="hv-map__legend-title">
              {filter.maxNightMinC != null
                ? `Nachttief ${['heute', 'morgen', 'übermorgen'][filter.nightIndex ?? 0]} höchstens ${formatTemp(filter.maxNightMinC)}C`
                : WEATHER_VALUES.find(([k]) => k === mapView.wxValue)?.[1]}
            </div>
            <div class="hv-map__legend-ramp" style={{ background: `linear-gradient(90deg, ${TEMP_STOPS.map(([, c]) => c).join(', ')})` }} />
            <div class="hv-map__legend-scale"><span>{formatTemp(TEMP_STOPS[0][0])}</span><span>0°</span><span>{formatTemp(TEMP_STOPS[TEMP_STOPS.length - 1][0])}</span></div>
            <div class="hv-map__legend-note">{weather.attribution}{weather.updatedAt ? `, Stand ${new Date(weather.updatedAt).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}` : ''}</div>
          </div>
        )}

        {error !== null && (
          <div class="hv-map__error" role="status">Anlagen konnten nicht geladen werden.</div>
        )}
      </div>
    </AppShell>
  );
}

function hasActiveFilter(f: MapFilterSpec): boolean {
  return countActiveDims(f) > 0;
}

function countActiveDims(f: MapFilterSpec): number {
  let n = 0;
  if (f.minElevationM != null || f.maxElevationM != null) n++;
  if (f.regionIso && f.regionIso.length > 0) n++;
  if (f.countryIso2 && f.countryIso2.length > 0) n++;
  if (f.modeIn24h && f.modeIn24h.length > 0) n++;
  if (f.minHeatingMinutes24h != null) n++;
  if (f.onlyWithActiveAlarms) n++;
  if (f.maxOfflineFor) n++;
  if (f.states && f.states.length > 0) n++;
  if (f.equipment && f.equipment.length > 0) n++;
  if (f.onlyServiceActive) n++;
  if (f.deviceKinds && f.deviceKinds.length > 0) n++;
  if (f.nameContains && f.nameContains.trim()) n++;
  if (f.maxNightMinC != null) n++;
  return n;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function FilterDrawer({
  filter,
  onChange,
  onClose,
  onSaveClick,
  resultCount,
}: {
  filter: MapFilterSpec;
  onChange: (next: MapFilterSpec) => void;
  onClose: () => void;
  onSaveClick: () => void;
  resultCount: number | null;
}) {
  // Local state for text inputs so the number-parsing doesn't fight the
  // controlled input on every keystroke (empty string → null → refetch).
  const [minElev, setMinElev] = useState<string>(filter.minElevationM?.toString() ?? '');
  const [maxElev, setMaxElev] = useState<string>(filter.maxElevationM?.toString() ?? '');
  const [minHeat, setMinHeat] = useState<string>(filter.minHeatingMinutes24h?.toString() ?? '');
  const [name, setName] = useState<string>(filter.nameContains ?? '');
  const [nightMax, setNightMax] = useState<string>(filter.maxNightMinC?.toString() ?? '');

  useEffect(() => {
    setName(filter.nameContains ?? '');
    setNightMax(filter.maxNightMinC?.toString() ?? '');
    setMinElev(filter.minElevationM?.toString() ?? '');
    setMaxElev(filter.maxElevationM?.toString() ?? '');
    setMinHeat(filter.minHeatingMinutes24h?.toString() ?? '');
  }, [filter]);

  const commit = (patch: Partial<MapFilterSpec>) => onChange({ ...filter, ...patch });

  const toggleIn = (key: 'countryIso2' | 'regionIso' | 'states' | 'equipment' | 'deviceKinds', value: string) => {
    const current = new Set(filter[key] ?? []);
    if (current.has(value)) current.delete(value); else current.add(value);
    commit({ [key]: current.size === 0 ? null : Array.from(current) } as Partial<MapFilterSpec>);
  };
  const toggleCountry = (iso: string) => toggleIn('countryIso2', iso);
  const has = (key: 'regionIso' | 'states' | 'equipment' | 'deviceKinds', value: string) =>
    (filter[key] ?? []).includes(value);

  // Auspraegungen nur fuer gewaehlte Kategorien; "HeatPump:Air" verengt "HeatPump".
  const pickedCats = DEVICE_CATEGORIES.filter((c) => c.variants && has('equipment', c.key)
    || (filter.equipment ?? []).some((e) => e.startsWith(`${c.key}:`)));
  const toggleVariant = (cat: string, variant: string) => {
    const key = `${cat}:${variant}`;
    const next = new Set(filter.equipment ?? []);
    if (next.has(key)) {
      next.delete(key);
      if (![...next].some((e) => e.startsWith(`${cat}:`))) next.add(cat);
    } else {
      next.delete(cat);
      next.add(key);
    }
    commit({ equipment: next.size === 0 ? null : Array.from(next) });
  };
  const toggleCategory = (cat: string) => {
    const next = new Set(filter.equipment ?? []);
    const on = next.has(cat) || [...next].some((e) => e.startsWith(`${cat}:`));
    for (const e of [...next]) if (e === cat || e.startsWith(`${cat}:`)) next.delete(e);
    if (!on) next.add(cat);
    commit({ equipment: next.size === 0 ? null : Array.from(next) });
  };
  const regionCountries = filter.countryIso2?.length ? filter.countryIso2.filter((c) => REGIONS[c]) : ['AT'];

  const parseNumber = (s: string): number | null => {
    const t = s.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  return (
    <aside class="hv-map__drawer" role="dialog" aria-label="Karten-Filter">
      <header class="hv-map__drawer-head">
        <h2>Filter</h2>
        <button type="button" class="hv-map__drawer-close" onClick={onClose} aria-label="Filter schließen">×</button>
      </header>

      <div class="hv-map__drawer-body">
        <section class="hv-map__field">
          <label class="hv-map__field-label" for="nameInput">Anlage</label>
          <input
            id="nameInput"
            type="text"
            placeholder="Name enthält …"
            value={name}
            onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
            onBlur={() => commit({ nameContains: name.trim() || null })}
            onKeyDown={(e) => { if (e.key === 'Enter') commit({ nameContains: name.trim() || null }); }}
          />
        </section>

        <section class="hv-map__field">
          <label class="hv-map__field-label">Zustand</label>
          <div class="hv-map__chips">
            {STATE_CHIPS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                class={`hv-map__chip hv-map__chip--state hv-map__chip--${key}${has('states', key) ? ' hv-map__chip--on' : ''}`}
                onClick={() => toggleIn('states', key)}
              >
                <span class="hv-map__chip-dot" aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
          <label class="hv-map__field-check">
            <input
              type="checkbox"
              checked={filter.onlyServiceActive === true}
              onChange={(e) => commit({ onlyServiceActive: (e.currentTarget as HTMLInputElement).checked ? true : null })}
            />
            Nur mit offenem Servicezugang
          </label>
        </section>

        <section class="hv-map__field">
          <label class="hv-map__field-label">Geräte</label>
          <div class="hv-map__chips">
            {DEVICE_CATEGORIES.map((c) => {
              const on = (filter.equipment ?? []).some((e) => e === c.key || e.startsWith(`${c.key}:`));
              return (
                <button
                  key={c.key}
                  type="button"
                  class={`hv-map__chip hv-map__chip--icon${on ? ' hv-map__chip--on' : ''}`}
                  onClick={() => toggleCategory(c.key)}
                  title={c.label}
                >
                  <span class="hv-map__chip-icon" aria-hidden="true" dangerouslySetInnerHTML={{ __html: iconSvg(iconKey(c.key), 14) }} />
                  {c.label}
                </button>
              );
            })}
          </div>
          {pickedCats.map((c) => (
            <div key={c.key} class="hv-map__subchips">
              <span class="hv-map__subchips-label">{c.label}:</span>
              {c.variants!.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  class={`hv-map__chip hv-map__chip--small${has('equipment', `${c.key}:${v.key}`) ? ' hv-map__chip--on' : ''}`}
                  onClick={() => toggleVariant(c.key, v.key)}
                >
                  {v.label}
                </button>
              ))}
            </div>
          ))}
        </section>

        <section class="hv-map__field">
          <label class="hv-map__field-label">Client</label>
          <div class="hv-map__chips">
            {[['gateway', 'Gateway'], ['panel', 'Touch-Panel']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                class={`hv-map__chip${has('deviceKinds', key) ? ' hv-map__chip--on' : ''}`}
                onClick={() => toggleIn('deviceKinds', key)}
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        <section class="hv-map__field">
          <label class="hv-map__field-label">Land</label>
          <div class="hv-map__chips">
            {['AT', 'DE', 'CH'].map((iso) => {
              const on = (filter.countryIso2 ?? []).includes(iso);
              return (
                <button
                  key={iso}
                  type="button"
                  class={`hv-map__chip${on ? ' hv-map__chip--on' : ''}`}
                  onClick={() => toggleCountry(iso)}
                >
                  {iso}
                </button>
              );
            })}
          </div>
        </section>

        <section class="hv-map__field">
          <label class="hv-map__field-label">Bundesland / Kanton</label>
          {regionCountries.map((c) => (
            <div key={c} class="hv-map__chips">
              {REGIONS[c].map(([iso, label]) => (
                <button
                  key={iso}
                  type="button"
                  class={`hv-map__chip hv-map__chip--small${has('regionIso', iso) ? ' hv-map__chip--on' : ''}`}
                  onClick={() => toggleIn('regionIso', iso)}
                >
                  {label}
                </button>
              ))}
            </div>
          ))}
        </section>

        <section class="hv-map__field">
          <label class="hv-map__field-label">Höhe (m ü. NN)</label>
          <div class="hv-map__field-row">
            <input
              type="number"
              placeholder="min"
              value={minElev}
              onInput={(e) => setMinElev((e.currentTarget as HTMLInputElement).value)}
              onBlur={() => commit({ minElevationM: parseNumber(minElev) })}
            />
            <span class="hv-map__field-sep">bis</span>
            <input
              type="number"
              placeholder="max"
              value={maxElev}
              onInput={(e) => setMaxElev((e.currentTarget as HTMLInputElement).value)}
              onBlur={() => commit({ maxElevationM: parseNumber(maxElev) })}
            />
          </div>
        </section>

        <section class="hv-map__field">
          <label class="hv-map__field-label" for="nightMaxInput">Wetter: Nachttief höchstens (°C)</label>
          <input
            id="nightMaxInput"
            type="number"
            step="0.5"
            placeholder="z.B. 0"
            value={nightMax}
            onInput={(e) => setNightMax((e.currentTarget as HTMLInputElement).value)}
            onBlur={() => commit({ maxNightMinC: parseNumber(nightMax) })}
            onKeyDown={(e) => { if (e.key === 'Enter') commit({ maxNightMinC: parseNumber(nightMax) }); }}
          />
          <div class="hv-map__chips">
            {['Heute Nacht', 'Morgen Nacht', 'Übermorgen'].map((label, i) => (
              <button
                key={label}
                type="button"
                class={`hv-map__chip hv-map__chip--small${(filter.nightIndex ?? 0) === i ? ' hv-map__chip--on' : ''}`}
                onClick={() => commit({ nightIndex: i === 0 ? null : i })}
              >
                {label}
              </button>
            ))}
          </div>
          <span class="hv-map__field-hint">Nacht von 18 bis 9 Uhr, auf der Höhe der Anlage gerechnet. Zusammen mit der Höhe etwa: über 1000 m und Frost.</span>
        </section>

        <section class="hv-map__field hv-map__field--disabled" title="Die Heizminuten werden noch nicht je Anlage berechnet.">
          <label class="hv-map__field-label" for="minHeatInput">Min. Heiz-Minuten (24 h)</label>
          <input
            id="minHeatInput"
            type="number"
            min="0"
            placeholder="z.B. 30"
            disabled
            value={minHeat}
          />
          <span class="hv-map__field-hint">Noch nicht verfügbar: die Heizminuten werden noch nicht je Anlage berechnet.</span>
        </section>
      </div>

      <footer class="hv-map__drawer-foot">
        <div class="hv-map__result-count">
          {resultCount === null ? 'Lade…' : `${resultCount} Anlage${resultCount === 1 ? '' : 'n'}`}
        </div>
        <button type="button" class="hv-map__ghost" onClick={() => onChange(EMPTY_SPEC)}>
          Zurücksetzen
        </button>
        <button type="button" class="hv-map__primary" onClick={onSaveClick}>
          Als Ansicht speichern
        </button>
      </footer>
    </aside>
  );
}

function SaveViewModal({
  onSave,
  onClose,
}: {
  onSave: (name: string, sharedWithScope: boolean) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState<string>('');
  const [shared, setShared] = useState<boolean>(false);
  const canSave = name.trim().length > 0;

  return (
    <div class="hv-map__modal-backdrop" role="dialog" aria-modal="true" aria-label="Ansicht speichern">
      <div class="hv-map__modal">
        <h3>Kartenansicht speichern</h3>
        <label class="hv-map__field-label" for="viewNameInput">Name</label>
        <input
          id="viewNameInput"
          type="text"
          value={name}
          autoFocus
          onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
        />
        <label class="hv-map__field-check">
          <input type="checkbox" checked={shared} onChange={(e) => setShared((e.currentTarget as HTMLInputElement).checked)} />
          Fuer alle im Team sichtbar
        </label>
        <div class="hv-map__modal-actions">
          <button type="button" class="hv-map__ghost" onClick={onClose}>Abbrechen</button>
          <button type="button" class="hv-map__primary" disabled={!canSave} onClick={() => onSave(name.trim(), shared)}>
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Christl, die Kartenassistentin (23.10.2026). Der Techniker beschreibt seine
 * Aufgabe, der Server fragt Claude (nur Frage und Filterbeschreibung, keine
 * Anlagendaten) und bekommt einen Filter zurueck. Der wird sofort angewandt
 * und laesst sich zuruecknehmen; entschieden wird nichts.
 */
function ChristlPanel({
  filter,
  onApply,
  onShowFilter,
  onClose,
}: {
  filter: MapFilterSpec;
  onApply: (f: MapFilterSpec) => void;
  onShowFilter: () => void;
  onClose: () => void;
}) {
  const [prompt, setPrompt] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [answer, setAnswer] = useState<SuggestMapFilterResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [before, setBefore] = useState<MapFilterSpec | null>(null);

  const ask = async () => {
    const q = prompt.trim();
    if (q.length < 3 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<SuggestMapFilterResponse>('/api/map/ai-filter', { prompt: q });
      setAnswer(r);
      setBefore(filter);
      const next: MapFilterSpec = { ...EMPTY_SPEC };
      for (const [k, v] of Object.entries(r.filter)) {
        if (v !== null && v !== undefined) (next as Record<string, unknown>)[k] = v;
      }
      onApply(next);
    } catch (e) {
      setError((e as Error).message || 'Christl ist gerade nicht erreichbar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="hv-map__view-pop hv-map__christl" role="dialog" aria-label="Christl, KI-Assistentin">
      <div class="hv-map__christl-head">
        <strong>Christl</strong>
        <span class="hv-map__christl-tag">KI-Assistentin</span>
        <button type="button" class="hv-map__drawer-close" onClick={onClose} aria-label="Christl schließen">×</button>
      </div>
      <p class="hv-map__christl-intro">
        Beschreiben Sie, welche Anlagen Sie suchen. Christl stellt den Filter zusammen, Sie können ihn danach ändern.
      </p>
      <textarea
        class="hv-map__christl-input"
        rows={3}
        maxLength={600}
        placeholder="z.B. Luftwärmepumpen über 1000 m, bei denen es heute Nacht friert"
        value={prompt}
        onInput={(e) => setPrompt((e.currentTarget as HTMLTextAreaElement).value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(); } }}
        disabled={busy}
      />
      <div class="hv-map__christl-actions">
        <button type="button" class="hv-map__primary" onClick={() => void ask()} disabled={busy || prompt.trim().length < 3}>
          {busy ? 'Christl überlegt …' : 'Filter vorschlagen'}
        </button>
      </div>
      {error && <div class="hv-map__christl-error" role="alert">{error}</div>}
      {answer && !busy && (
        <div class="hv-map__christl-answer" aria-live="polite">
          <p>{answer.explanation}</p>
          {answer.notPossible && <p class="hv-map__christl-muted">Nicht als Filter möglich: {answer.notPossible}</p>}
          {answer.ignored.length > 0 && (
            <p class="hv-map__christl-muted">Verworfen, weil unbekannt: {answer.ignored.join(', ')}</p>
          )}
          <div class="hv-map__christl-actions">
            <button type="button" class="hv-map__ghost" onClick={onShowFilter}>Filter ansehen</button>
            {before && (
              <button type="button" class="hv-map__ghost" onClick={() => { onApply(before); setBefore(null); setAnswer(null); }}>
                Rückgängig
              </button>
            )}
          </div>
        </div>
      )}
      <div class="hv-map__view-note">An die KI gehen nur Ihre Frage und die Beschreibung der Filter, keine Anlagendaten.</div>
    </div>
  );
}

/** Kleine Auswahl aus Chips, eine Option aktiv. */
function SegChips({ value, options, onChange }: {
  value: string;
  options: [string, string][];
  onChange: (v: string) => void;
}) {
  return (
    <div class="hv-map__chips">
      {options.map(([k, label]) => (
        <button
          key={k}
          type="button"
          class={`hv-map__chip hv-map__chip--small${value === k ? ' hv-map__chip--on' : ''}`}
          aria-pressed={value === k}
          onClick={() => onChange(k)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
